/**
 * Search Results page — displays global search results with categorized sections.
 *
 * Features:
 * - Search bar with typeahead
 * - Categorized results (Vendors, Contracts, Contacts, etc.)
 * - Recent searches display
 *
 * Requirements: 30.1, 30.2, 30.3, 30.5
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface SearchResultItem {
  entityType: string;
  entityId: string;
  title: string;
  subtitle: string | null;
  matchField: string;
  url: string;
}

interface SearchResultsData {
  query: string;
  totalResults: number;
  categories: Record<string, SearchResultItem[]>;
}

interface RecentSearch {
  searchId: number;
  searchTerm: string;
  resultCount: number;
  searchedAt: string;
}

const ENTITY_LABELS: Record<string, string> = {
  Vendor: 'Vendors',
  Contract: 'Contracts',
  VendorContact: 'Contacts',
  ComplianceDocument: 'Compliance Documents',
  RiskAlert: 'Risk Alerts',
  Amendment: 'Amendments',
  Employee: 'Employees',
  Asset: 'Hardware Assets',
  Site: 'Sites',
  Circuit: 'Circuits',
  AuditLog: 'Audit Log',
};

export function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') ?? '');
  const [results, setResults] = useState<SearchResultsData | null>(null);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [typeaheadResults, setTypeaheadResults] = useState<SearchResultItem[]>([]);
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch recent searches on mount
  useEffect(() => {
    fetch('/api/search/recent', { credentials: 'include' })
      .then((res) => res.json())
      .then(setRecentSearches)
      .catch(() => {});
  }, []);

  // Perform search when URL query changes
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(term)}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setResults(data);
      }
    } catch {
      // Silently handle search errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setSearchTerm(q);
      performSearch(q);
    }
  }, [searchParams, performSearch]);

  // Typeahead
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setTypeaheadResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(searchTerm)}&typeahead=true`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setTypeaheadResults(data);
        }
      } catch {
        // Silently handle typeahead errors
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setSearchParams({ q: searchTerm.trim() });
      setShowTypeahead(false);
    }
  };

  const handleResultClick = (item: SearchResultItem) => {
    navigate(item.url);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-lg font-semibold mb-4">Search</h1>

      {/* Search Bar */}
      <form onSubmit={handleSubmit} className="relative mb-6">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowTypeahead(true);
          }}
          onFocus={() => setShowTypeahead(true)}
          onBlur={() => setTimeout(() => setShowTypeahead(false), 200)}
          placeholder="Search vendors, contracts, contacts..."
          className="w-full border rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-baker-accent"
          aria-label="Global search"
        />
        <button
          type="submit"
          className="absolute right-3 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-baker-red text-white rounded hover:bg-baker-red/90 text-sm"
        >
          Search
        </button>

        {/* Typeahead Dropdown */}
        {showTypeahead && typeaheadResults.length > 0 && (
          <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg mt-1 max-h-64 overflow-y-auto">
            {typeaheadResults.map((item, i) => (
              <button
                key={`${item.entityType}-${item.entityId}-${i}`}
                onMouseDown={() => handleResultClick(item)}
                className="w-full text-left px-4 py-2 hover:bg-page flex justify-between items-center"
              >
                <div>
                  <div className="text-sm font-medium">{item.title}</div>
                  {item.subtitle && <div className="text-xs text-slate-500">{item.subtitle}</div>}
                </div>
                <span className="text-xs text-slate-400 bg-page px-2 py-0.5 rounded">
                  {ENTITY_LABELS[item.entityType] ?? item.entityType}
                </span>
              </button>
            ))}
          </div>
        )}
      </form>

      {/* Loading */}
      {loading && <p className="text-slate-500">Searching...</p>}

      {/* Results */}
      {results && !loading && (
        <div>
          <p className="text-sm text-slate-500 mb-4">
            {results.totalResults} result{results.totalResults !== 1 ? 's' : ''} for "{results.query}"
          </p>

          {Object.entries(results.categories).map(([category, items]) => (
            <div key={category} className="mb-6">
              <h2 className="text-lg font-medium mb-2 border-b pb-1">
                {ENTITY_LABELS[category] ?? category} ({items.length})
              </h2>
              <ul className="space-y-2">
                {items.map((item, i) => (
                  <li key={`${item.entityId}-${i}`}>
                    <button
                      onClick={() => handleResultClick(item)}
                      className="w-full text-left p-3 rounded hover:bg-page border"
                    >
                      <div className="font-medium text-baker-red">{item.title}</div>
                      {item.subtitle && <div className="text-sm text-slate-500">{item.subtitle}</div>}
                      <div className="text-xs text-slate-400 mt-1">Matched on: {item.matchField}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {results.totalResults === 0 && (
            <p className="text-slate-500">No results found. Try a different search term.</p>
          )}
        </div>
      )}

      {/* Recent Searches (shown when no active search) */}
      {!results && !loading && recentSearches.length > 0 && (
        <div>
          <h2 className="text-lg font-medium mb-3">Recent Searches</h2>
          <ul className="space-y-1">
            {recentSearches.map((rs) => (
              <li key={rs.searchId}>
                <button
                  onClick={() => {
                    setSearchTerm(rs.searchTerm);
                    setSearchParams({ q: rs.searchTerm });
                  }}
                  className="text-baker-red hover:underline text-sm"
                >
                  {rs.searchTerm}
                </button>
                <span className="text-xs text-slate-400 ml-2">
                  {rs.resultCount} results
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
